import * as THREE from "three";
import { resolveEndEffectorBodyUuid } from "../endEffectorResolve";
import { logger } from "../../utils/logger";
import type { BTLeaf } from "../types";
import type { LeafContext, TickResult } from "./context";
import {
  distance,
  resolveRuntimeBodyPos,
  resolveRuntimeBodyTargetPos,
  resolveRuntimeSitePosByName,
} from "./helpers";

export function tickConditionTimeElapsed(
  ctx: LeafContext,
  node: Extract<BTLeaf, { kind: "conditionTimeElapsed" }>
): TickResult {
  const lm = ctx.getLeafMem(node.id);
  const now = performance.now();
  if (lm.timeElapsedStartMs == null) {
    lm.timeElapsedStartMs = now;
  }
  const sec = Math.max(0, Number(node.seconds) || 0);
  if (now - (lm.timeElapsedStartMs ?? now) >= sec * 1000) {
    ctx.clearLeafMem(node.id);
    return "success";
  }
  return "failure";
}

export function tickConditionObjectGrasped(
  ctx: LeafContext,
  node: Extract<BTLeaf, { kind: "conditionObjectGrasped" }>
): TickResult {
  const p = resolveRuntimeBodyPos(node.bodyUuid);
  if (!p) {
    ctx.setFailureReason("Condition failed: object body was not found in runtime state.");
    return "failure";
  }
  return p.y >= Number(node.minHeightY) ? "success" : "failure";
}

function resolveAnchoredTarget(
  _ctx: LeafContext,
  targetBodyUuid: string | undefined,
  x: number,
  y: number,
  z: number
): { target: THREE.Vector3 } | { error: string } {
  if (!targetBodyUuid) {
    return { target: new THREE.Vector3(x, y, z) };
  }
  const anchor = resolveRuntimeBodyTargetPos(targetBodyUuid);
  if (!anchor) {
    return { error: `target body "${targetBodyUuid}" could not be resolved in the runtime scene.` };
  }
  return { target: new THREE.Vector3(anchor.x + x, anchor.y + y, anchor.z + z) };
}

export function tickConditionObjectNear(
  ctx: LeafContext,
  node: Extract<BTLeaf, { kind: "conditionObjectNear" }>
): TickResult {
  const p = resolveRuntimeBodyPos(node.bodyUuid);
  if (!p) {
    ctx.setFailureReason("Condition failed: object body was not found in runtime state.");
    return "failure";
  }
  const resolved = resolveAnchoredTarget(ctx, node.targetBodyUuid, node.x, node.y, node.z);
  if ("error" in resolved) {
    ctx.setFailureReason(`Condition failed: ${resolved.error}`);
    return "failure";
  }
  return distance(p, resolved.target) <= Number(node.toleranceM) ? "success" : "failure";
}

export function tickConditionEeNear(
  ctx: LeafContext,
  node: Extract<BTLeaf, { kind: "conditionEeNear" }>
): TickResult {
  const lm = ctx.getLeafMem(node.id);
  const now = performance.now();
  const resolved = resolveEndEffectorBodyUuid(ctx.scene, node.endEffectorRef);
  if (!resolved) {
    ctx.setFailureReason(
      `Condition failed: end-effector ref "${node.endEffectorRef}" was not found.`
    );
    return "failure";
  }
  const p =
    resolved.useGripperTargetSite && node.endEffectorRef === "gripper_target"
      ? resolveRuntimeSitePosByName(ctx.scene, "gripper_target")
      : resolveRuntimeBodyPos(resolved.bodyUuid);
  if (!p) {
    ctx.setFailureReason("Condition failed: end-effector body has no runtime pose.");
    return "failure";
  }
  const target = resolveAnchoredTarget(ctx, node.targetBodyUuid, node.x, node.y, node.z);
  if ("error" in target) {
    ctx.setFailureReason(`Condition failed: ${target.error}`);
    return "failure";
  }
  const d = distance(p, target.target);
  const tol = Number(node.toleranceM);
  const ok = d <= tol;
  if (!ok && (lm.conditionDiagLastAtMs == null || now - lm.conditionDiagLastAtMs >= 400)) {
    lm.conditionDiagLastAtMs = now;
    logger.info("[behavior/gate-diag]", {
      nodeId: node.id,
      label: node.label || null,
      kind: node.kind,
      endEffectorRef: node.endEffectorRef,
      targetBodyUuid: node.targetBodyUuid || null,
      distanceM: Number.isFinite(d) ? Number(d.toFixed(4)) : null,
      toleranceM: Number.isFinite(tol) ? Number(tol.toFixed(4)) : null,
      ee: {
        x: Number(p.x.toFixed(4)),
        y: Number(p.y.toFixed(4)),
        z: Number(p.z.toFixed(4)),
      },
      target: {
        x: Number(target.target.x.toFixed(4)),
        y: Number(target.target.y.toFixed(4)),
        z: Number(target.target.z.toFixed(4)),
      },
    });
  }
  return ok ? "success" : "failure";
}
